import { useRouter } from 'next/router';
import React, {
  useEffect, useRef, useState,
} from 'react';

import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { readerSelector } from '~/atomic/page/book-reader/reader.selectors';
import { readingPositionSync } from '~/atomic/page/book-reader/services/readingPositionSync';
import { MAX_LOCAL_DATA_AGE } from '~/atomic/page/book-reader/services/readingPositionTypes';
import { bookSelector } from '~/feature/book/book.selector';
import { chapterSelector } from '~/feature/chapter/chapter.selector';
import { saveShowBookInECommerce } from '~/feature/finance/ECommerce/saveShowBookInECommerce';
import { viewChapter } from '~/feature/statistics/viewChapter';
import { useAppDispatch, useAppSelector } from '~/store';

import { Reader } from './Reader';

export const ReaderTextBook = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { currentChapter } = useAppSelector(chapterSelector);
  const { currentGraphqlBook } = useAppSelector(bookSelector);
  const { isChapterNavigation } = useAppSelector(readerSelector);
  // Состояние для блокировки рендера до редиректа на сохранённую главу
  const [isPendingRedirect, setIsPendingRedirect] = useState(true);
  const hasCheckedSavedPositionRef = useRef(false);

  // Проверяем сохранённую позицию и редиректим ДО рендера контента
  useEffect(() => {
    if (hasCheckedSavedPositionRef.current) {
      return;
    }
    if (!currentGraphqlBook?.id || !currentGraphqlBook?.slug) {
      return;
    }
    if (!currentChapter.chapterId) {
      return;
    }

    hasCheckedSavedPositionRef.current = true;

    const localPosition = readingPositionSync.getLocalPosition(currentGraphqlBook.id);
    const savedChapterId = localPosition
      && localPosition.timestamp > Date.now() - MAX_LOCAL_DATA_AGE
      ? Number(localPosition.chapterId)
      : null;

    // Если это навигация между главами (клик на "следующая/предыдущая глава"),
    // не восстанавливаем позицию — позиция уже задана через ?page=first в URL
    if (isChapterNavigation) {
      setIsPendingRedirect(false);
      return;
    }

    // Если есть сохранённая позиция и она отличается от текущей главы — редиректим
    if (savedChapterId && savedChapterId !== Number(currentChapter.chapterId)) {
      // Добавляем ?page= если есть сохранённая страница для book format
      const pageParam = localPosition?.bookFormatPage !== undefined && localPosition.bookFormatPage > 0
        ? `?page=${localPosition.bookFormatPage}`
        : '';
      const targetUrl = `${pagesConfigStore.readerChapter.as(currentGraphqlBook.slug, savedChapterId)}${pageParam}`;
      router.replace(`${pagesConfigStore.readerChapter.url}${pageParam}`, targetUrl, { shallow: true });
      // isPendingRedirect останется true, пока не загрузится правильная глава
    } else if (savedChapterId && savedChapterId === Number(currentChapter.chapterId)) {
      // Та же глава, но возможно нужно восстановить страницу
      const pageParam = localPosition?.bookFormatPage !== undefined && localPosition.bookFormatPage > 0
        ? `?page=${localPosition.bookFormatPage}`
        : '';
      if (pageParam && !router.asPath.includes('?page=')) {
        const targetUrl = `${pagesConfigStore.readerChapter.as(currentGraphqlBook.slug, savedChapterId)}${pageParam}`;
        router.replace(`${pagesConfigStore.readerChapter.url}${pageParam}`, targetUrl, { shallow: true });
      }
      setIsPendingRedirect(false);
    } else {
      // Нет редиректа — можно показывать контент
      setIsPendingRedirect(false);
    }
  }, [currentGraphqlBook?.id, currentGraphqlBook?.slug, currentChapter.chapterId, router, isChapterNavigation]);

  // Сбрасываем isPendingRedirect когда URL изменился на правильную главу
  useEffect(() => {
    if (!isPendingRedirect) return;
    if (!currentGraphqlBook?.id) return;

    const localPosition = readingPositionSync.getLocalPosition(currentGraphqlBook.id);
    const savedChapterId = localPosition
      && localPosition.timestamp > Date.now() - MAX_LOCAL_DATA_AGE
      ? Number(localPosition.chapterId)
      : null;

    // Если текущая глава совпадает с сохранённой (или нет сохранённой) — показываем контент
    if (!savedChapterId || savedChapterId === Number(currentChapter.chapterId)) {
      setIsPendingRedirect(false);
    }
  }, [currentChapter.chapterId, currentGraphqlBook?.id, isPendingRedirect]);

  useEffect(() => {
    // Статистика - некритичная операция, откладываем в фон
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleCallbackId = window.requestIdleCallback(() => {
        dispatch(saveShowBookInECommerce({ book: currentGraphqlBook }));
        if (currentChapter.chapterId) {
          dispatch(viewChapter({ chapterId: currentChapter.chapterId }));
        }
      }, { timeout: 2000 });

      return () => window.cancelIdleCallback(idleCallbackId);
    }
    const timeoutId = setTimeout(() => {
      dispatch(saveShowBookInECommerce({ book: currentGraphqlBook }));
      if (currentChapter.chapterId) {
        dispatch(viewChapter({ chapterId: currentChapter.chapterId }));
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [currentGraphqlBook?.slug]);

  // Восстановление позиции для обычного текстового режима
  // происходит в ReaderTextChapter.tsx

  // Восстановление разворота для книжного формата
  // происходит через ?page= в URL, который читает ReaderContent.tsx
  // Сброс страницы при смене главы происходит в changeReaderChapter (reader.data.ts)

  // Блокируем рендер контента до завершения редиректа на сохранённую главу
  if (isPendingRedirect) {
    return null; // Или можно показать лоадер: <ReaderLoader />
  }

  return (
    <Reader />
  );
};
